import _ from 'lodash';

import { getValueOrDash } from 'utils/data';
import { formatCurrencyValue, formatPercentValue } from 'utils/format';

import { DrawRequestStatus } from 'store/construction/types';

const PERCENT_SCALE = 100;

export const formatMoney = value => getValueOrDash(formatCurrencyValue(value));

export const formatPercent = value => getValueOrDash(formatPercentValue(value));

const sumBy = (lines, key) => lines.reduce((total, line) => total + (line[key] ?? 0), 0);

// Budget totals are derived from the cost-coded lines so the summary row and the stat cards stay
// consistent with whatever line set the API returns, even before the API sends its own rollups.
export const getBudgetTotals = (lines = []) => {
  const budgeted = sumBy(lines, 'budgeted');
  const fundedToDate = sumBy(lines, 'fundedToDate');
  const thisDraw = sumBy(lines, 'thisDraw');
  const remaining = sumBy(lines, 'remaining');
  const percentComplete = budgeted ? Math.round((fundedToDate / budgeted) * PERCENT_SCALE) : 0;

  return { budgeted, fundedToDate, thisDraw, remaining, percentComplete };
};

export const getClampedPercent = value => {
  if (_.isNil(value)) {
    return 0;
  }

  return Math.max(0, Math.min(PERCENT_SCALE, value));
};

export const getNetDisbursement = drawRequest => {
  if (_.isNil(drawRequest)) {
    return null;
  }

  if (!_.isNil(drawRequest.netToDisburse)) {
    return drawRequest.netToDisburse;
  }

  const requested = drawRequest.requested ?? 0;
  const retainage = drawRequest.retainageHeld ?? 0;

  return requested - retainage;
};

export const getPendingBlockingChecklistItems = (checklistItems = []) =>
  checklistItems.filter(item => item.isBlocking && !item.isCleared);

export const isDrawApprovalBlocked = (checklistItems = []) =>
  getPendingBlockingChecklistItems(checklistItems).length > 0;

// A rejected draw never progressed past review, so it anchors the stepper at the Under Review stage
// rather than falling off the five-stage lifecycle.
export const getDrawStepperActiveStatus = status =>
  status === DrawRequestStatus.REJECTED ? DrawRequestStatus.UNDER_REVIEW : status;

// The API refuses to create a new draw while a prior one is still open (only Disbursed/Rejected are
// terminal) — check this client-side so "Add Draw Request" doesn't round-trip into a 409 Conflict.
const TERMINAL_DRAW_STATUSES = [DrawRequestStatus.DISBURSED, DrawRequestStatus.REJECTED];

export const getHasActiveDrawRequest = (drawRequests = []) =>
  drawRequests.some(drawRequest => !TERMINAL_DRAW_STATUSES.includes(drawRequest.status));
